import { mergeRefs } from "@react-aria/utils";
import clsx from "clsx";
import { forwardRef } from "react";
import { TextArea as AriaTextArea, type TextAreaProps as AriaTextAreaProps } from "react-aria-components";
import { type Control, Controller, type FieldPath, type FieldValues } from "react-hook-form";

import {
  type TextInputVariantProps,
  TextInputWrapper,
  type TextInputWrapperProps,
  textInput,
} from "./TextInputWrapper";

type TextAreaInputProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = {
  customRef?: React.ForwardedRef<HTMLTextAreaElement>;
  formControl?: {
    control: Control<TFieldValues>;
    name: TName;
  };
} & Omit<TextInputVariantProps, "element"> &
  Omit<TextInputWrapperProps, "children"> &
  AriaTextAreaProps;

const TextAreaInner = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  customRef,
  variant,
  textfieldClassName,
  ...props
}: Omit<TextAreaInputProps<TFieldValues, TName>, "formControl">) => {
  return (
    <TextInputWrapper
      {...props}
      textfieldClassName="flex h-full flex-col items-start justify-center"
    >
      <AriaTextArea
        {...props}
        className={clsx(textInput({ variant }), textfieldClassName)}
        ref={customRef}
      />
    </TextInputWrapper>
  );
};

const TextAreaWithRef = <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
  { formControl, ...props }: Omit<TextAreaInputProps<TFieldValues, TName>, "customRef" | "field">,
  ref: React.ForwardedRef<HTMLTextAreaElement>,
) => {
  if (formControl) {
    return (
      <Controller
        control={formControl.control}
        name={formControl.name}
        render={({ field: { ref: controllerRef, ...field }, fieldState: { error } }) => (
          <TextAreaInner
            {...props}
            field={field}
            customRef={mergeRefs(ref, controllerRef)}
            error={props.error ?? error?.message}
          />
        )}
      />
    );
  }

  return (
    <TextAreaInner
      {...props}
      customRef={ref}
    />
  );
};

// When using it with React Hook Form, you should pass `formControl` prop instead of `{...register("name")}`.
// Before: `const { register } = useForm(); <TextArea {...register("description")} />`
// After: `const { control } = useForm(); <TextArea formControl={{ control, name: "description" }} />`
export const TextArea = forwardRef(TextAreaWithRef) as <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(
  props: Omit<TextAreaInputProps<TFieldValues, TName>, "customRef" | "field"> & {
    ref?: React.ForwardedRef<HTMLTextAreaElement>;
  },
) => ReturnType<typeof TextAreaWithRef>;
